import React, { useEffect, useMemo } from 'react';
import { SimpleWizardPageProps } from '../../../../common/components/simple-wizard/simple-wizard';
import {
  Application,
  Catalogue,
  InputField,
  FormLayout,
  HeadingLayout,
  HeadingOne,
  getTitleListFromSolutions,
} from 'src/features/common';
import { Field, FieldArray } from 'formik';
import { Delivery, Solution } from '../../../../common/types/application/opportunity';
import {
  getCatalogueItemFromCatalogue,
  isVirtualDevice,
} from '../../../../common/helpers/get-device-from-catalogue';
import {
  getProductKey,
  getSolutionMapFromSolutionArray,
  getSolutionsDeliveryBreakdown,
  ProductKey,
} from '../../../helpers/delivery-helpers';
import SpinEditReskin from '../../../../../components/spin-edit-reskin/spin-edit-reskin';
import { ShowIf } from '../../../../../components';
import { Toast } from 'src/features/common/components/toast/toast';
import { DeliveryOptions } from 'src/features/common/types/application/delivery-options';
import { MiniAppFocusWrapper } from 'src/components/miniapp-focus-wrapper/miniapp-focus-wrapper';
import './modal-step-three.scss';
import '../delivery-address-modal.scss';
import { useApplication } from 'src/hooks/useApplication';

export type ModalStepThreeProps = {
  wizardProps: SimpleWizardPageProps;
  delivery: Delivery;
  catalogue: Catalogue;
  application: Application;
  deliveryExisting?: Delivery;
  automationIdSuffix: string;
};

export function ModalStepThree({
  delivery,
  catalogue,
  application,
  deliveryExisting,
  automationIdSuffix,
}: ModalStepThreeProps) {
  const { orderTealiumService } = useApplication();
  const { solutions, solutionsWithoutAddresses } = getSolutionsDeliveryBreakdown(
    application,
    catalogue,
  );

  useEffect(() => {
    orderTealiumService?.onAddressDeliveryDeviceAllocationStart();
  }, []);

  const deliverableSolutions = solutions.filter((sol) => {
    const device = getTitleListFromSolutions([sol], catalogue);

    return (
      device?.[0]?.deliveryOptions?.includes(DeliveryOptions.Deliver) &&
      !isVirtualDevice(device?.[0]?.title)
    );
  });

  const solutionsWithoutAddressesAndNotYetStaged = useMemo<Solution[]>(() => {
    const stagedCurrentMap = getSolutionMapFromSolutionArray(delivery.solutions);
    const solutionsWithoutAddressesAndNotYetStaged = solutionsWithoutAddresses.filter(
      (solution) => {
        const key: ProductKey = getProductKey(solution.productId, solution.acquiringChannel);
        const isStaged = stagedCurrentMap.has(key);
        return !isStaged;
      },
    );
    return solutionsWithoutAddressesAndNotYetStaged;
  }, [delivery, solutionsWithoutAddresses]);

  const canSolutionBeDelivered = (solution: Solution): boolean => {
    const catalogueItem = getCatalogueItemFromCatalogue(
      catalogue,
      solution.productId,
      solution.acquiringChannel,
    );

    return (
      !!catalogueItem &&
      !catalogueItem.device.productCategoryName &&
      !isVirtualDevice(catalogueItem.device.title)
    );
  };

  return (
    <article
      className={`ModalStepThree ModalStepRevamp ${
        application.brokerLogin ? 'ModalStepThreeBroker' : ''
      }`}
    >
      <Toast />
      <HeadingLayout
        renderMainHeading={() => (
          <HeadingOne>
            {application.brokerLogin
              ? 'Select the amount of devices and the delivery address'
              : 'Which devices do you want to send to this address?'}
          </HeadingOne>
        )}
      />

      {application.brokerLogin && deliveryExisting && (
        <p className="existing-address">{`${deliveryExisting?.address.street}, ${deliveryExisting?.address.suburb}, ${deliveryExisting?.address.city}`}</p>
      )}
      <section className="scrollable-area">
        <FieldArray name="solutions">
          {({ push: addSolution, remove: removeSolutionAtIndex }) => {
            const deviceDropDownValues = application.brokerLogin
              ? deliverableSolutions.map((solution) => ({
                  ...solution,
                  quantity: 1,
                }))
              : solutionsWithoutAddressesAndNotYetStaged
                  .filter(canSolutionBeDelivered)
                  .map((solution) => ({
                    ...solution,
                    quantity: 1,
                  }));

            const _defaultAddress = [
              application.contact?.businessAddress?.street || '',
              application.contact?.businessAddress?.suburb || '',
              application.contact?.businessAddress?.city || '',
            ];

            const deliveries = application.brokerLogin
              ? (application.opportunity?.deliveries || [])
                  .map((delivery) => ({
                    id: delivery.id,
                    address: `${delivery.address.street}, ${delivery.address.suburb}, ${delivery.address.city}`,
                  }))
                  .filter((_address) => !_address.address.includes('undefined'))
              : [];

            deliveries.unshift({
              id: 'default',
              address: _defaultAddress.join(', '),
            });

            return (
              <>
                {delivery.solutions.map((solution, index) => {
                  const currentSolutionFromSolutionsDeliveryBreakdown: Solution | undefined =
                    solutions.find(
                      (solutionFromSolutionsDeliveryBreakdown) =>
                        solutionFromSolutionsDeliveryBreakdown.productId === solution.productId,
                    );

                  const maxQuantity: number =
                    currentSolutionFromSolutionsDeliveryBreakdown?.quantity ?? 0;

                  return (
                    <FormLayout
                      key={`${getProductKey(
                        solution.productId,
                        solution.acquiringChannel,
                      )}-index-${index}`}
                    >
                      <section className={'solution-quantity-rows'}>
                        <Field
                          name={`solutions[${index}]`}
                          type="text"
                          placeholder="Select a device"
                          items={deviceDropDownValues}
                          getItemDisplayValue={(item: Solution) =>
                            getCatalogueItemFromCatalogue(
                              catalogue,
                              item.productId,
                              item.acquiringChannel,
                            )?.device?.title || ''
                          }
                          component={MiniAppFocusWrapper}
                          innerComponent={InputField}
                          fieldType="select"
                          isDark={false}
                          automationid={`${automationIdSuffix}_Step_Three_${index + 1}_Suburb`}
                        />

                        {application.brokerLogin && (
                          <Field
                            name={`newDelivery[${index}]`}
                            type="text"
                            className={'address-input'}
                            placeholder="Select address"
                            items={deliveries}
                            getItemDisplayValue={(item: { address: string; id: string }) =>
                              deliveries.find((x) => x.id === item.id)?.address
                            }
                            component={MiniAppFocusWrapper}
                            innerComponent={InputField}
                            fieldType="select"
                            isDark={false}
                            automationid={`${automationIdSuffix}_Step_Three3_${index + 1}_Suburb`}
                          />
                        )}

                        <div className="counter-and-cross">
                          <Field
                            name={`solutions[${index}].quantity`}
                            component={SpinEditReskin}
                            disabled={!(solution.acquiringChannel && solution.productId)}
                            min={1}
                            max={maxQuantity}
                            lightMode={true}
                            automationid={`${automationIdSuffix}_Step_Three_${index + 1}_Quantity`}
                          />
                          {delivery.solutions.length > 1 && (
                            <span
                              className="icon-cross"
                              onClick={() => removeSolutionAtIndex(index)}
                              automationid={`${automationIdSuffix}_Step_Three_${
                                index + 1
                              }_Remove_Solution`}
                            />
                          )}
                        </div>
                        <div className="line is-hidden-tablet" />
                      </section>
                    </FormLayout>
                  );
                })}

                <ShowIf
                  condition={
                    delivery.solutions.length < solutions.filter(canSolutionBeDelivered).length &&
                    !application.brokerLogin
                  }
                >
                  <div className="add-staged-address-button has-text-centered">
                    <div
                      className="icon-plus"
                      onClick={() => addSolution({})}
                      automationid={`${automationIdSuffix}_Step_Three_Add_Solution`}
                    />
                    <div className="tiny-sub-heading">Add device</div>
                  </div>
                </ShowIf>
              </>
            );
          }}
        </FieldArray>
      </section>
    </article>
  );
}
